import SortableList from '@components/SortableList';
import Tip from '@components/Tip';
import { TipWrapper } from '@components/TipWrapper';
import IconBatch from '@components/icons/IconBatch';
import IconGroup from '@components/icons/IconGroup';
import { $tourDataApi } from '@stores/dataStores';
import {
  $editingImageMarker,
  $editingImageMarkerApi,
} from '@stores/editingMarkerStores';
import {
  $stImageSettingPanel,
  $stImageSettingPanelApi,
} from '@stores/sandTableStores';
import type { STImage, STMatrix } from '@types';
import arrayMove from '@utils/arrayMove';
import { useStoreMap } from 'effector-react';
import { useEffect } from 'react';
import { Scrollbars } from 'react-custom-scrollbars';
import type { SortEnd } from 'react-sortable-hoc';
import SidebarHeader from '../../sidebar/components/SidebarHeader';
import { showCompassSetting } from '../CompassWidget';
import { PanelSandTableImage } from '../PanelSandTableImage';
import { PanelSandTableImageSetting } from '../PanelSandTableImageSetting';
import { stEditorKrpPlayer } from '../STKrpPlayer';
import { MarkerAddButton } from '../components/MarkerAddButton';
import { MAX_MARKERS } from '../constants';
import Item from './Item';
import newMarkerData from './newMarkerData';

const typeItems = [
  { name: '单个添加', isVip: false },
  { name: '批量添加', isVip: false },
] as const;

interface Props {
  data: STMatrix | STImage;
  onBatchClick: () => void;
  onGroupClick: () => void;
}

export function SidebarSandTableImage({
  data,
  onBatchClick,
  onGroupClick,
}: Props) {
  const { markers, id, angle } = data;
  const isMax = markers.length >= MAX_MARKERS;
  const isEditing = useStoreMap({
    store: $editingImageMarker,
    keys: [],
    fn: (state) => !!state,
  });
  const isSetting = useStoreMap({
    store: $stImageSettingPanel,
    keys: [],
    fn: (state) => !!state,
  });

  useEffect(() => {
    if (angle === undefined) {
      showCompassSetting({ sandTableId: id });
    }
  }, []);

  useEffect(() => {
    markers.forEach((x, i) => {
      stEditorKrpPlayer.addMarker(x, { zOrder: i, dragable: true });
    });
    return () => {
      $stImageSettingPanelApi.off();
    };
  }, []);

  const handleSortEnd = (sort: SortEnd) => {
    const newMarkers = arrayMove({
      array: markers,
      fromIndex: sort.oldIndex,
      toIndex: sort.newIndex,
    });
    $tourDataApi.updateSandtable({
      id,
      updater: (draft) => {
        draft.markers = newMarkers;
      },
    });
    stEditorKrpPlayer.reOrderMarkers(newMarkers);
  };

  const handleAddClick = ({ batchMode }: { batchMode: boolean }) => {
    const newMarker = newMarkerData();
    if (!newMarker) return;
    $editingImageMarkerApi.set({
      marker: newMarker,
      isNew: true,
      zOrder: markers.length,
      batchMode,
    });
  };

  return (
    <>
      <div className="absolute inset-0 flex flex-col">
        <SidebarHeader
          title={
            <div className="flex">
              <div>标记点</div>
              <Tip className="ml-10 self-end text-base">
                {`${markers.length}/${MAX_MARKERS}`}
              </Tip>
            </div>
          }
          btns={
            <>
              <TipWrapper
                className="mr-10"
                text="标记点分组设置"
                placement="bottom"
                size="sm"
              >
                <IconGroup
                  className="focus:outline-none"
                  onClick={onGroupClick}
                />
              </TipWrapper>
              <TipWrapper text="批量操作" placement="bottom" size="sm">
                <IconBatch
                  className="focus:outline-none"
                  onClick={onBatchClick}
                />
              </TipWrapper>
            </>
          }
        />
        <MarkerAddButton
          items={typeItems}
          disabled={isMax}
          onSelect={({ name: val }) => {
            if (val === '单个添加') {
              handleAddClick({ batchMode: false });
            } else if (val === '批量添加') {
              handleAddClick({ batchMode: true });
            }
          }}
        />
        {markers.length > 0 ? (
          <div className="relative mt-10 flex-1">
            <SortableList
              axis="y"
              lockAxis="y"
              distance={10}
              onSortEnd={handleSortEnd}
            >
              <Scrollbars>
                {markers.map((x, i) => (
                  <Item
                    index={i}
                    key={x.id}
                    marker={x}
                    listIndex={i}
                    sandTableId={id}
                  />
                ))}
              </Scrollbars>
            </SortableList>
          </div>
        ) : (
          <>
            <Tip className="mt-15 text-center text-base">
              {`每个沙盘最多可添加${MAX_MARKERS}个标记点`}
            </Tip>
            <Tip className="mt-5 text-center text-base">
              添加后支持上下拖拽调整标记点顺序
            </Tip>
          </>
        )}
      </div>
      {isEditing && <PanelSandTableImage sandTableId={id} />}
      {isSetting && <PanelSandTableImageSetting />}
    </>
  );
}

export default SidebarSandTableImage;
